import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Sparkles, Wrench, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ROUTES, BASE_URL } from '../seo/routeMetadata';
import { CHANGELOG } from '../seo/changelog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  feature: { label: 'New', icon: Sparkles, className: 'bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/30' },
  improvement: { label: 'Improved', icon: Zap, className: 'bg-[#F97316]/15 text-[#F97316] border-[#F97316]/30' },
  fix: { label: 'Fixed', icon: Wrench, className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
};

const fmtDateLong = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
};

const groupByMonth = (entries) => {
  const groups = new Map();
  for (const e of entries) {
    const d = new Date(e.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    if (!groups.has(key)) groups.set(key, { label, items: [] });
    groups.get(key).items.push(e);
  }
  return Array.from(groups.values());
};

const ChangelogPage = () => {
  const meta = ROUTES['/changelog'];
  const grouped = useMemo(() => groupByMonth(CHANGELOG), []);
  const latest = CHANGELOG[0]?.date;

  // Verify admin via /api/user/profile — the same endpoint ProfilePage uses.
  // Unlike /api/auth/me, this accepts both cookie sessions (Google OAuth)
  // and Bearer JWTs (email/password), so admins from either flow work.
  const [authState, setAuthState] = useState({ loading: true, isAdmin: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API_URL}/api/user/profile`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('unauthorized');
        const data = await res.json();
        if (!cancelled) setAuthState({ loading: false, isAdmin: !!data?.is_admin });
      } catch {
        if (!cancelled) setAuthState({ loading: false, isAdmin: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (authState.loading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }
  if (!authState.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        url={`${BASE_URL}${meta.path}`}
        jsonLd={meta.jsonLd}
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <header className="mb-10 pb-8 border-b border-[#1e2a45]">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-gray-500">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-gray-600">›</span>
            <span className="text-gray-400">Changelog</span>
          </nav>

          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-3" data-testid="page-h1">
            {meta.h1}
          </h1>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-6">
            {latest && (
              <>
                <span>Last update <time dateTime={latest}>{fmtDateLong(latest)}</time></span>
                <span aria-hidden="true">·</span>
              </>
            )}
            <span>{CHANGELOG.length} releases</span>
            <span aria-hidden="true">·</span>
            <Link to="/learn" className="hover:text-white">Learn hub</Link>
          </div>

          <p className="text-lg text-gray-200 leading-relaxed">{meta.aeoAnswer}</p>
        </header>

        {grouped.map((group) => (
          <section key={group.label} className="mb-12">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              {group.label}
            </h2>
            <ol className="relative border-l border-[#1e2a45] pl-6 space-y-6">
              {group.items.map((entry, idx) => {
                const t = TYPE_META[entry.type] || TYPE_META.improvement;
                const Icon = t.icon;
                return (
                  <li
                    key={`${entry.date}-${idx}`}
                    data-testid={`changelog-entry-${entry.date}-${idx}`}
                    className="relative"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute -left-[33px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#050810] ring-2 ring-[#1e2a45]"
                    >
                      <Icon className="h-2.5 w-2.5 text-gray-500" />
                    </span>
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${t.className}`}
                      >
                        {t.label}
                      </span>
                      <time
                        dateTime={entry.date}
                        className="text-xs text-gray-500"
                      >
                        {fmtDateLong(entry.date)}
                      </time>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">{entry.title}</h3>
                    <p className="text-sm text-gray-300 leading-relaxed">{entry.summary}</p>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}

        <div className="mt-12 rounded-2xl border border-[#F97316]/30 bg-gradient-to-r from-[#F97316]/10 to-[#3B82F6]/10 p-6 text-center">
          <p className="text-gray-300 mb-4">Want product updates in your inbox?</p>
          <Link
            to="/contact"
            data-testid="changelog-cta-contact"
            className="inline-block bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 rounded-full font-semibold"
          >
            Get in touch
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ChangelogPage;
